<template>
  <!-- Loading Screen -->
  <div v-if="isLoading" class="loading-screen">
    <img src="@/assets/hamster.gif" alt="Loading..." />
    <p>Generating report, please wait...</p>
  </div>

  <!-- Main Reports Page -->
  <div v-else class="reports-page">
    <h1 class="reports-title">Reports</h1>

    <!-- Daily Reports Section -->
    <div class="report-generation-box">
      <h2>Daily Reports</h2>
      <div class="form-group-container">
        <!-- Date Selector -->
        <div class="form-group">
          <label>Report Date</label>
          <DatePicker v-model="selectedDate" @change="handleDateChange" width="260px" />
        </div>
        <!-- Report Type Selector -->
        <div class="form-group">
          <label for="reportType">Report Type</label>
          <select id="reportType" class="form-control" v-model="selectedReportType">
            <option value="all">All Reports</option>
            <option v-for="type in reportTypes" :key="type" :value="type">{{ type }}</option>
          </select>
        </div>
        <!-- Generate Report Button -->
        <div class="form-group">
          <label class="invisible-label">Generate</label>
          <button class="btn-generate-report" @click="generateReports" :disabled="isLoading">Generate/Download Reports</button>
        </div>
      </div>
    </div>

    <!-- Year End Reports Section -->
    <div class="year-end-reports-box">
      <h2>Year End Reports</h2>
      <div class="form-group-container">
        <!-- Year Start Selector -->
        <div class="form-group">
          <label>Year Start</label>
          <DatePicker v-model="yearStart" width="260px" />
        </div>
        <!-- Year End Selector -->
        <div class="form-group">
          <label>Year End</label>
          <DatePicker v-model="yearEnd" width="260px" />
        </div>
        <!-- Report Type Selector -->
        <div class="form-group">
          <label for="yearEndReportType">Report Type</label>
          <select id="yearEndReportType" class="form-control" v-model="selectedYearEndReportType">
            <option value="all">All Reports</option>
            <option v-for="type in yearEndReportTypes" :key="type" :value="type">{{ type }}</option>
          </select>
        </div>
        <!-- Generate Report Button -->
        <div class="form-group">
          <label class="invisible-label">Generate</label>
          <button class="btn-generate-report" @click="generateYearEndReports" :disabled="isLoading">Generate/Download Reports</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ReportsDataService from '@/services/ReportsDataService.js';
import DatePicker from '../Shared/DatePicker.vue';
import { todayLocalDateString } from '@/utils/dateHelpers';
import {
  generateDailyTripsReport,
  generateMedicalReport,
  generateLunchReport,
  generateShuttleReport,
  generateGroupListReport,
  generateRentalReport,
  generateWildlifeReport,
  generateZoneUsageReport,
  generateSkierDaysReport,
  generateRunUsageReport
} from './reportScripts';

export default {
  components: { DatePicker },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      selectedReportType: 'all', // Single selection instead of array
      selectedDate: this.getStoredDate() || todayLocalDateString(),
      reportTypes: [
        'Guide Report',
        'Medical Report',
        'Lunch Report',
        'Shuttle Report',
        'Group List Report',
        'Rental Report'
      ],
      selectedYearEndReportType: 'all', // Single selection instead of array
      yearStart: `${currentYear}-01-01`,
      yearEnd: `${currentYear}-12-31`,
      yearEndReportTypes: [
        'Wildlife Report',
        'Zone Usage Report',
        'Skier Days Report',
        'Run Usage Report'
      ],
      isLoading: false // Add loading state
    };
  },

  methods: {
    getStoredDate() {
      return sessionStorage.getItem('selectedDate');
    },
    storeDate(date) {
      sessionStorage.setItem('selectedDate', date);
    },
    handleDateChange() {
      this.storeDate(this.selectedDate);
    },
    async generateReports() {
      // Validate that a date is selected
      if (!this.selectedDate) {
        alert('Please select a report date before generating reports.');
        return;
      }

      // Validate that a report type is selected
      if (!this.selectedReportType) {
        alert('Please select a report type before generating reports.');
        return;
      }

      this.isLoading = true;

      try {
        // Determine which reports to generate
        let reportsToGenerate = [];
        if (this.selectedReportType === 'all') {
          reportsToGenerate = [...this.reportTypes];
        } else {
          reportsToGenerate = [this.selectedReportType];
        }

        // Process reports sequentially to avoid conflicts
        for (const reportType of reportsToGenerate) {
          switch (reportType) {
            case 'Guide Report':
              await this.generateDateTripsOverview();
              break;
            case 'Medical Report':
              await this.generateDateMedicalReport();
              break;
            case 'Lunch Report':
              await this.generateDateLunchReport();
              break;
            case 'Shuttle Report':
              await this.generateDateShuttleReport();
              break;
            case 'Group List Report':
              await this.generateGroupListReport();
              break;
            case 'Rental Report':
              await this.generateDateRentalReport();
              break;
          }
          // Add a small delay between reports to ensure proper download handling
          if (reportsToGenerate.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      } catch (error) {
        console.error('Error generating reports:', error);
        alert('An error occurred while generating reports. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },
    async generateYearEndReports() {
      // Validate that both year start and year end dates are provided
      if (!this.yearStart || !this.yearEnd) {
        alert('Please select both Year Start and Year End dates before generating reports.');
        return;
      }

      // Validate that year start is before year end
      if (new Date(this.yearStart) > new Date(this.yearEnd)) {
        alert('Year Start date must be before Year End date.');
        return;
      }

      // Validate that a report type is selected
      if (!this.selectedYearEndReportType) {
        alert('Please select a report type before generating reports.');
        return;
      }

      this.isLoading = true;

      try {
        // Determine which reports to generate
        let reportsToGenerate = [];
        if (this.selectedYearEndReportType === 'all') {
          reportsToGenerate = [...this.yearEndReportTypes];
        } else {
          reportsToGenerate = [this.selectedYearEndReportType];
        }

        // Process reports sequentially to avoid conflicts
        for (const reportType of reportsToGenerate) {
          switch (reportType) {
            case 'Wildlife Report':
              await this.generateYearEndWildlifeReport();
              break;
            case 'Zone Usage Report':
              await this.generateYearEndZoneUsageReport();
              break;
            case 'Skier Days Report':
              await this.generateYearEndSkierDaysReport();
              break;
            case 'Run Usage Report':
              await this.generateYearEndRunUsageReport();
              break;
          }
          // Add a small delay between reports to ensure proper download handling
          if (reportsToGenerate.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      } catch (error) {
        console.error('Error generating year-end reports:', error);
        alert('An error occurred while generating reports. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },
    generateYearEndWildlifeReport() {
      return ReportsDataService.getWildlifeExportReportData(this.yearStart, this.yearEnd)
        .then(response => {
          const wildlifeData = response.data;
          generateWildlifeReport(wildlifeData);
        })
        .catch(error => {
          console.error("Error fetching wildlife data for year-end report:", error);
          throw error;
        });
    },
    generateYearEndZoneUsageReport() {
      return ReportsDataService.getZoneUsageReportData(this.yearStart, this.yearEnd)
        .then(response => {
          const zoneUsageData = response.data;
          generateZoneUsageReport(zoneUsageData);
        })
        .catch(error => {
          console.error("Error fetching zone usage data for year-end report:", error);
          throw error;
        });
    },
    generateYearEndSkierDaysReport() {
      return ReportsDataService.getSkierDaysReportData(this.yearStart, this.yearEnd)
        .then(response => {
          const skierDaysData = response.data;
          generateSkierDaysReport(skierDaysData);
        })
        .catch(error => {
          console.error("Error fetching skier days data for year-end report:", error);
          throw error;
        });
    },
    generateYearEndRunUsageReport() {
      return ReportsDataService.getRunUsageReportData(this.yearStart, this.yearEnd)
        .then(response => {
          const runUsageData = response.data;

          if (!runUsageData || !runUsageData.reportData) {
            throw new Error('Invalid report data received from server');
          }

          try {
            generateRunUsageReport(runUsageData);
            console.log('Run Usage Report generated successfully!');
          } catch (pdfError) {
            console.error("Error generating PDF:", pdfError);
            throw new Error('Failed to generate PDF: ' + pdfError.message);
          }
        })
        .catch(error => {
          console.error("Error with run usage report:", error);
          console.error(`Failed to generate Run Usage Report: ${error.response?.data?.message || error.message || 'Unknown error'}`);
          throw error;
        });
    },

    generateDateTripsOverview() {
      return ReportsDataService.getDailyTripsReportData(this.selectedDate)
        .then(response => {
          const tripsData = response.data;
          const sortedTripsData = tripsData.sort((a, b) => a.heliIndex - b.heliIndex);
          sortedTripsData.forEach(trip => {
            trip.groups = trip.groups.sort((a, b) => a.groupId - b.groupId);
          });
          generateDailyTripsReport(sortedTripsData, this.selectedDate);
        })
        .catch(error => {
          console.error("Error fetching trips for report:", error);
          throw error;
        });
    },
    generateDateLunchReport() {
      return ReportsDataService.getLunchReportData(this.selectedDate)
        .then(response => {
          const lunchData = response.data;
          const sortedLunchData = lunchData.sort((a, b) => a.heliIndex - b.heliIndex);
          sortedLunchData.forEach(trip => {
            trip.groups = trip.groups.sort((a, b) => a.groupId - b.groupId);
          });
          generateLunchReport(sortedLunchData, this.selectedDate);
        })
        .catch(error => {
          console.error("Error fetching trips for report:", error);
          throw error;
        });
    },
    generateDateMedicalReport() {
      return ReportsDataService.getMedicalReportData(this.selectedDate)
        .then(response => {
          // Medical report backend returns { reportData, totalGuides, heliBreakdown, groupBreakdown }
          const medicalData = response.data.reportData || response.data;
          const sortedMedicalData = medicalData.sort((a, b) => a.heliIndex - b.heliIndex);
          sortedMedicalData.forEach(trip => {
            trip.groups = trip.groups.sort((a, b) => a.groupId - b.groupId);
          });
          console.log(sortedMedicalData);
          generateMedicalReport(sortedMedicalData, this.selectedDate);
        })
        .catch(error => {
          console.error("Error fetching trips for report:", error);
          throw error;
        });
    },
    generateDateShuttleReport() {
      return ReportsDataService.getDailyShuttleReportData(this.selectedDate)
        .then(response => {
          const shuttleData = response.data;
          const sortedShuttleData = shuttleData.sort((a, b) => {
            if (a.heliIndex === b.heliIndex) {
              return a.groupIndex - b.groupIndex;
            }
            return a.heliIndex - b.heliIndex;
          });
          console.log(sortedShuttleData);
          generateShuttleReport(sortedShuttleData, this.selectedDate);
        })
        .catch(error => {
          console.error("Error fetching shuttle data for report:", error);
          throw error;
        });
    },
    generateGroupListReport() {
      return ReportsDataService.getGroupListReportData(this.selectedDate)
        .then(response => {
          const groupListData = response.data;
          const sortedGroupListData = groupListData.sort((a, b) => a.heliIndex - b.heliIndex);
          sortedGroupListData.forEach(trip => {
            trip.groups = trip.groups.sort((a, b) => a.groupId - b.groupId);
          });
          generateGroupListReport(sortedGroupListData, this.selectedDate);
        })
        .catch(error => {
          console.error("Error fetching group list data for report:", error);
          throw error;
        });
    },
    generateDateRentalReport() {
      return ReportsDataService.getDailyRentalReportData(this.selectedDate)
        .then(response => {
          const rentalData = response.data;
          const sortedRentalData = rentalData.sort((a, b) => a.heliIndex - b.heliIndex);
          sortedRentalData.forEach(trip => {
            trip.groups = trip.groups.sort((a, b) => a.groupId - b.groupId);
          });
          generateRentalReport(sortedRentalData, this.selectedDate);
        })
        .catch(error => {
          console.error("Error fetching rental data for report:", error);
          throw error;
        });
    },
    generateDateWildlifeReport() {
      ReportsDataService.getWildlifeExportReportData(this.selectedDate)
        .then(response => {
          const wildlifeData = response.data;
          generateWildlifeReport(wildlifeData);
        })
        .catch(error => {
          console.error("Error fetching wildlife data for report:", error);
        });
    },
    testReport() {
      console.log("Testing Report");
      ReportsDataService.getLunchReportData(this.selectedDate)
        .then(response => {
          console.log('Test Report Data:', response.data);
        })
        .catch(error => {
          console.error("Error during test fetch for report:", error);
        });
    },
  },
};
</script>

<style scoped>
.reports-page {
  margin: 20px;
}

.reports-title {
  text-align: center;
  margin-bottom: 20px;
}

.year-end-reports-box,
.report-generation-box {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin-bottom: 20px;
  position: relative;
}

.year-end-reports-box h2,
.report-generation-box h2 {
  position: absolute;
  top: 10px;
  left: 20px;
  margin: 0;
  font-size: 1.25rem;
}

.form-group-container {
  display: flex;
  align-items: flex-start; /* Align the items at the top */
  margin-top: 40px; /* Provide some space for the h2 */
}

.form-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: 20px;
}

.invisible-label {
  visibility: hidden;
}

#reportType,
#yearEndReportType {
  box-sizing: border-box;
  width: 260px;
  padding: 10px 12px;
  font-size: 1rem;
  line-height: 1.5;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-bottom: 0;
}

.btn-generate-report {
  box-sizing: border-box;
  width: 260px;
  padding: 10px 12px;
  font-size: 1rem;
  line-height: 1.5;
  border: 1px solid transparent;
  border-radius: 5px;
  color: #fff;
  background-color: var(--primary-text-eph-color);
  cursor: pointer;
  align-self: flex-start;
}

.previous-reports {
  margin-top: 20px;
  background-color: #fff;
}

.table {
  width: 100%;
  margin-bottom: 1rem;
  background-color: transparent;
}

.table th,
.table td {
  padding: 0.75rem;
  border-top: 1px solid #eceeef;
}

.table thead th {
  border-bottom: 2px solid #eceeef;
}

.table tbody + tbody {
  border-top: 2px solid #eceeef;
}

.btn-download {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: pointer;
}

.btn-test-report {
  padding: 0.375rem 1rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  color: #fff;
  background-color: #28a745;
  border: none;
  cursor: pointer;
  margin-top: 10px;
}

.loading-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  text-align: center;
}

.loading-screen img {
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
}

.btn-generate-report:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

</style>
